import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'
import type {
  AspectRatio,
  StudioAsset,
  GenerateStudioImageInput,
  StudioMode,
  StudioProject,
  StudioStylePreset,
} from '../domain/studioTypes'
import {
  deleteStudioProject,
  generateStudioImage,
  importStudioImage,
  listStudioProjects,
  reviseStudioImage,
  syncStudioProjects,
  watchStudioProjects,
} from '../lib/studioApi'
import { haptic } from '../../lib/native'
import { useAuth } from '../../contexts/AuthContext'
import { useVoxnela } from '../../contexts/VoxnelaContext'
import { useUiText } from '../../i18n/ui'
import { formatStudioMessage, studioMessages } from '../../i18n/studio'
import { getProjectStyleTitle } from '../lib/studioImageDetails'

const STORAGE_KEY = 'voxnela_studio_projects_cloud_v2'
const userStorageKey = (userId: string) => `${STORAGE_KEY}_${userId}`
const legacyStorageKeys = (userId: string) => [
  'voxnela_studio_projects_v1',
  `voxnela_studio_projects_v1_${userId}`,
]
const migratedStorageKey = (userId: string) => `voxnela_studio_legacy_migrated_v2_${userId}`

export interface ActiveReferencePreset {
  imageUrl: string
  variation?: boolean
  aspectRatio?: AspectRatio
  entryOrigin?: 'gallery'
  displayTitle?: string
  description?: string
  projectId?: string
  version?: number
  label?: string
}

interface StudioWorkspaceContextValue {
  projects: StudioProject[]
  activeProject: StudioProject | null
  activeProjectId: string | null
  setActiveProjectId: (id: string | null) => void
  activeReferencePreset: ActiveReferencePreset | null
  setActiveReferencePreset: (ref: ActiveReferencePreset | null) => void
  sheetOpen: boolean
  setSheetOpen: (open: boolean) => void
  sheetMode: StudioMode
  setSheetMode: (mode: StudioMode) => void
  selectedSubcategory: string | null
  setSelectedSubcategory: (subcategoryId: string | null) => void
  presetPrompt: string | null
  setPresetPrompt: (prompt: string | null) => void
  activeStylePreset: StudioStylePreset | null
  styleEntryOrigin: 'external' | 'sheet' | null
  setActiveStylePreset: (style: StudioStylePreset | null) => void
  openStudioWithPreset: (
    mode: StudioMode,
    subcategory?: string,
    presetPrompt?: string,
    stylePreset?: StudioStylePreset | null,
  ) => void
  openStudioWithProject: (project: StudioProject) => void
  openStudioWithVariation: (project: StudioProject) => void
  clearActiveProject: () => void
  createProject: (input: Omit<GenerateStudioImageInput, 'projectId'>) => Promise<StudioProject>
  importImage: (file: File) => Promise<StudioProject>
  reviseProject: (projectId: string, instruction: string, aspectRatio?: AspectRatio, requestId?: string, inputAssets?: StudioAsset[]) => Promise<StudioProject>
  setProjectVersion: (projectId: string, version: number) => void
  deleteProject: (projectId: string) => Promise<void>
  refreshProjects: () => Promise<{ total: number }>
  isGenerating: boolean
  isRevising: boolean
}

const StudioWorkspaceContext = createContext<StudioWorkspaceContextValue | null>(null)

export function StudioWorkspaceProvider({ children }: PropsWithChildren) {
  const { locale } = useUiText()
  const { user, loading: authLoading } = useAuth()
  const { refreshWorkspace } = useVoxnela()
  const copy = studioMessages[locale]
  const [projects, setProjects] = useState<StudioProject[]>([])
  const [projectsReadyForUser, setProjectsReadyForUser] = useState<string | null>(null)

  const [activeProjectId, setActiveProjectId] = useState<string | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [sheetMode, setSheetMode] = useState<StudioMode>('style_transfer')
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null)
  const [presetPrompt, setPresetPrompt] = useState<string | null>(null)
  const [activeStylePreset, setActiveStylePresetState] = useState<StudioStylePreset | null>(null)
  const [styleEntryOrigin, setStyleEntryOrigin] = useState<'external' | 'sheet' | null>(null)
  const setActiveStylePreset = useCallback((style: StudioStylePreset | null) => {
    setActiveStylePresetState(style)
    setStyleEntryOrigin(style ? 'sheet' : null)
  }, [])
  const [activeReferencePreset, setActiveReferencePreset] = useState<ActiveReferencePreset | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isRevising, setIsRevising] = useState(false)

  // Pull the signed-in user's cloud library onto this device, with local storage
  // serving as an offline fallback.
  useEffect(() => {
    if (authLoading) return
    let active = true
    let stopWatching: (() => void) | undefined
    setProjectsReadyForUser(null)
    if (!user) {
      setProjects([])
      return () => { active = false }
    }

    try {
      const stored = localStorage.getItem(userStorageKey(user.uid))
      setProjects(stored ? JSON.parse(stored) as StudioProject[] : [])
    } catch {
      setProjects([])
    }

    const loadCloudProjects = async () => {
      if (localStorage.getItem(migratedStorageKey(user.uid)) !== 'done') {
        try {
          const legacyProjects = legacyStorageKeys(user.uid).flatMap((key) => {
            try {
              const stored = JSON.parse(localStorage.getItem(key) || '[]') as StudioProject[]
              return Array.isArray(stored) ? stored : []
            } catch { return [] }
          })
          const uniqueCloudProjects = Array.from(new Map(legacyProjects
            .filter((project) => project?.id && project.versions?.length > 0 && project.versions.every((version) =>
              version.imageUrl.startsWith('https://firebasestorage.googleapis.com/'),
            ))
            .map((project) => [project.id, project])).values())
          if (uniqueCloudProjects.length > 0) {
            await syncStudioProjects(uniqueCloudProjects)
          }
          localStorage.setItem(migratedStorageKey(user.uid), 'done')
        } catch (error) {
          console.warn('[Voxnela Studio] Eski tarayıcı görselleri eşitlenemedi.', error)
        }
      }
      return listStudioProjects()
    }

    void loadCloudProjects().then((cloudProjects) => {
      if (active) setProjects(cloudProjects)
    }).catch((error) => {
      console.warn('[Voxnela Studio] Bulut görsel geçmişi yüklenemedi.', error)
    }).finally(() => {
      if (!active) return
      stopWatching = watchStudioProjects(user.uid, (cloudProjects) => {
        if (active) setProjects(cloudProjects)
      }, (error) => {
        console.warn('[Voxnela Studio] Canlı görsel eşitlemesi kesildi.', error)
      })
      setProjectsReadyForUser(user.uid)
    })
    return () => {
      active = false
      stopWatching?.()
    }
  }, [authLoading, user?.uid])

  const refreshProjects = useCallback(async () => {
    if (!user) throw new Error('Görselleri eşitlemek için aynı Voxnela hesabında oturum açmalısın.')
    const cloudProjects = await listStudioProjects()
    setProjects(cloudProjects)
    return { total: cloudProjects.length }
  }, [user])

  useEffect(() => {
    if (!user || authLoading) return
    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'visible') return
      void refreshProjects().catch((error) => {
        console.warn('[Voxnela Studio] Görseller arka plandan dönünce eşitlenemedi.', error)
      })
    }
    document.addEventListener('visibilitychange', refreshWhenVisible)
    window.addEventListener('focus', refreshWhenVisible)
    const refreshTimer = window.setInterval(refreshWhenVisible, 45_000)
    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible)
      window.removeEventListener('focus', refreshWhenVisible)
      window.clearInterval(refreshTimer)
    }
  }, [authLoading, refreshProjects, user])

  useEffect(() => {
    if (!user || projectsReadyForUser !== user.uid) return
    try {
      localStorage.setItem(userStorageKey(user.uid), JSON.stringify(projects))
    } catch {
      // Cloud library remains the source of truth when local storage is full.
    }
  }, [projects, projectsReadyForUser, user])

  const activeProject = useMemo(() => {
    return projects.find((p) => p.id === activeProjectId) ?? null
  }, [projects, activeProjectId])

  const openStudioWithPreset = useCallback((
    mode: StudioMode,
    subcategory?: string,
    prompt?: string,
    stylePreset?: StudioStylePreset | null,
  ) => {
    void haptic('selection')
    setSheetMode(mode)
    if (subcategory) {
      setSelectedSubcategory(subcategory)
    }
    if (prompt) {
      setPresetPrompt(prompt)
    } else {
      setPresetPrompt(null)
    }
    if (stylePreset !== undefined) {
      setActiveStylePresetState(stylePreset)
      setStyleEntryOrigin(stylePreset ? 'external' : null)
    }
    setActiveReferencePreset(null)
    setSheetOpen(true)
  }, [])

  const openStudioWithProject = useCallback((project: StudioProject) => {
    void haptic('selection')
    const currentVer = project.versions.find(
      (v) => v.version === project.currentVersion,
    ) ?? project.versions[project.versions.length - 1]

    setActiveProjectId(project.id)
    setActiveStylePresetState(null)
    setStyleEntryOrigin(null)
    setSheetMode(project.mode)
    setSelectedSubcategory(project.subcategory || null)
    setPresetPrompt(null)
    setActiveReferencePreset({
      imageUrl: currentVer?.imageUrl || "",
      projectId: project.id,
      version: currentVer?.version || project.currentVersion,
      label: `${formatStudioMessage(copy.versionReady, { version: currentVer?.version || project.currentVersion })}`,
      entryOrigin: 'gallery',
      displayTitle: getProjectStyleTitle(project, copy.defaultStyle, locale),
      description: copy.revisePlaceholder,
    })
    setSheetOpen(true)
  }, [copy.defaultStyle, copy.revisePlaceholder, copy.versionReady, locale])

  const openStudioWithVariation = useCallback((project: StudioProject) => {
    void haptic('selection')
    const version = project.versions.find((item) => item.version === project.currentVersion)
      ?? project.versions[project.versions.length - 1]
    if (!version) return
    setActiveProjectId(null)
    setActiveStylePresetState(null)
    setStyleEntryOrigin(null)
    setSheetMode(project.mode === 'portrait_retouch' ? 'style_transfer' : project.mode)
    setSelectedSubcategory(project.subcategory || null)
    setPresetPrompt(null)
    setActiveReferencePreset({
      imageUrl: version.imageUrl,
      variation: true,
      aspectRatio: project.aspectRatio,
      label: copy.referenceImage,
      entryOrigin: 'gallery',
      displayTitle: getProjectStyleTitle(project, copy.defaultStyle, locale),
      description: copy.transformStyleDetail,
    })
    setSheetOpen(true)
  }, [copy.defaultStyle, copy.referenceImage, copy.transformStyleDetail, locale])

  const clearActiveProject = useCallback(() => {
    setActiveProjectId(null)
    setActiveReferencePreset(null)
  }, [])

  const createProject = useCallback(async (
    input: Omit<GenerateStudioImageInput, 'projectId'>,
  ): Promise<StudioProject> => {
    setIsGenerating(true)
    const projectId = `proj_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`

    try {
      const { project } = await generateStudioImage({
        ...input,
        projectId,
      })
      setProjects((current) => [project, ...current.filter((p) => p.id !== projectId)])
      setActiveProjectId(project.id)
      setSheetOpen(true)
      void haptic('success')
      return project
    } finally {
      setIsGenerating(false)
      void refreshWorkspace().catch((error) => {
        console.warn('[Voxnela Studio] Kredi bakiyesi yenilenemedi.', error)
      })
    }
  }, [refreshWorkspace])

  const importImage = useCallback(async (file: File): Promise<StudioProject> => {
    const project = await importStudioImage(file)
    setProjects((current) => [project, ...current.filter((item) => item.id !== project.id)])
    void haptic('success')
    return project
  }, [])

  const reviseProject = useCallback(async (
    projectId: string,
    instruction: string,
    aspectRatio?: AspectRatio,
    requestId?: string,
    inputAssets?: StudioAsset[],
  ): Promise<StudioProject> => {
    const existing = projects.find((p) => p.id === projectId)
    if (!existing) throw new Error(copy.projectNotFound)

    setIsRevising(true)
    try {
      const { project } = await reviseStudioImage(
        {
          projectId,
          instruction,
          aspectRatio,
          requestId: requestId ?? `rev_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          inputAssets,
        },
        existing,
      )

      setProjects((current) => current.map((p) => (p.id === projectId ? project : p)))
      void haptic('success')
      return project
    } finally {
      setIsRevising(false)
      void refreshWorkspace().catch((error) => {
        console.warn('[Voxnela Studio] Kredi bakiyesi yenilenemedi.', error)
      })
    }
  }, [copy.projectNotFound, projects, refreshWorkspace])

  const setProjectVersion = useCallback((projectId: string, version: number) => {
    void haptic('selection')
    setProjects((current) =>
      current.map((p) => (p.id === projectId ? { ...p, currentVersion: version } : p)),
    )
  }, [])

  const deleteProject = useCallback(async (projectId: string) => {
    await deleteStudioProject(projectId)
    void haptic('selection')
    setProjects((current) => current.filter((p) => p.id !== projectId))
    if (activeProjectId === projectId) {
      setActiveProjectId(null)
    }
  }, [activeProjectId])

  const value = useMemo<StudioWorkspaceContextValue>(() => ({
    projects,
    activeProject,
    activeProjectId,
    setActiveProjectId,
    activeReferencePreset,
    setActiveReferencePreset,
    sheetOpen,
    setSheetOpen,
    sheetMode,
    setSheetMode,
    selectedSubcategory,
    setSelectedSubcategory,
    presetPrompt,
    setPresetPrompt,
    activeStylePreset,
    styleEntryOrigin,
    setActiveStylePreset,
    openStudioWithPreset,
    openStudioWithProject,
    openStudioWithVariation,
    clearActiveProject,
    createProject,
    importImage,
    reviseProject,
    setProjectVersion,
    deleteProject,
    refreshProjects,
    isGenerating,
    isRevising,
  }), [
    projects,
    activeProject,
    activeProjectId,
    activeReferencePreset,
    sheetOpen,
    sheetMode,
    selectedSubcategory,
    presetPrompt,
    activeStylePreset,
    styleEntryOrigin,
    setActiveStylePreset,
    openStudioWithPreset,
    openStudioWithProject,
    openStudioWithVariation,
    clearActiveProject,
    createProject,
    importImage,
    reviseProject,
    setProjectVersion,
    deleteProject,
    refreshProjects,
    isGenerating,
    isRevising,
  ])

  return (
    <StudioWorkspaceContext.Provider value={value}>
      {children}
    </StudioWorkspaceContext.Provider>
  )
}

const DEFAULT_STUDIO_WORKSPACE: StudioWorkspaceContextValue = {
  projects: [],
  activeProject: null,
  activeProjectId: null,
  setActiveProjectId: () => {},
  activeReferencePreset: null,
  setActiveReferencePreset: () => {},
  sheetOpen: false,
  setSheetOpen: () => {},
  sheetMode: 'style_transfer',
  setSheetMode: () => {},
  selectedSubcategory: null,
  setSelectedSubcategory: () => {},
  presetPrompt: null,
  setPresetPrompt: () => {},
  activeStylePreset: null,
  styleEntryOrigin: null,
  setActiveStylePreset: () => {},
  openStudioWithPreset: () => {},
  openStudioWithProject: () => {},
  openStudioWithVariation: () => {},
  clearActiveProject: () => {},
  createProject: async () => {
    throw new Error('StudioWorkspaceProvider not mounted')
  },
  importImage: async () => {
    throw new Error('StudioWorkspaceProvider not mounted')
  },
  reviseProject: async () => {
    throw new Error('StudioWorkspaceProvider not mounted')
  },
  setProjectVersion: () => {},
  deleteProject: async () => {},
  refreshProjects: async () => ({ total: 0 }),
  isGenerating: false,
  isRevising: false,
}

export function useStudioWorkspace(): StudioWorkspaceContextValue {
  const context = useContext(StudioWorkspaceContext)
  return context ?? DEFAULT_STUDIO_WORKSPACE
}
